import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import getBraintreeClientToken from '../../api/getBraintreeClientToken';
import processPlanUpgrade from '../../api/processPlanUpgrade';
import createPaymentMethodApi from '../../api/createPaymentMethod';

export default function useBraintreeDropIn() {
    const [clientToken, setClientToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
    const [hasPaymentFailure, setHasPaymentFailure] = useState(false);
    const [hasPaymentSuccess, setHasPaymentSuccess] = useState(false);
    const [isCreatePaymentMethodLoading, setIsCreatePaymentMethodLoading] = useState(false);
    const [hasCreatePaymentMethodFailure, setHasCreatePaymentMethodFailure] = useState(false);
    const [braintreeInstance, setBraintreeInstance] = useState<any>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
    const [paymentMethodIdByNonce, setPaymentMethodIdByNonce] = useState<Record<string, string>>({});

    const orgId = useSelector((state: any) => state.user.headCoach?.orgId);

    const processBraintree = async (newCoachLicenseId: number) => {
        if (!braintreeInstance) {
            return;
        }

        setIsPaymentProcessing(true);
        setErrorMessage(null);
        braintreeInstance.requestPaymentMethod(async (error: string, payload: any) => {
            if (error) {
                setIsPaymentProcessing(false);
                setErrorMessage('Credit card is invalid');
            } else {
                const { nonce } = payload;
                await fetch(`server.test/purchase/${nonce}`);
                const response = await processPlanUpgrade(nonce, orgId, newCoachLicenseId);

                if (response.status && response.status !== 200) {
                    setHasPaymentFailure(true);
                } else {
                    setHasPaymentSuccess(true);
                    setHasPaymentFailure(false);
                }

                setIsPaymentProcessing(false);
            }
        });
    };

    const processPurchase = async (newCoachLicenseId: number): Promise<boolean> => {
        if (!paymentMethodId) {
            setErrorMessage('Payment method is not created');
            setHasPaymentFailure(true);
            return false;
        }

        setIsPaymentProcessing(true);

        const response = await processPlanUpgrade('', orgId, newCoachLicenseId, paymentMethodId);

        if (response.status && response.status !== 200) {
            setHasPaymentFailure(true);
            setErrorMessage('Payment failed');
            setIsPaymentProcessing(false);
            return false;
        }

        setHasPaymentSuccess(true);
        setHasPaymentFailure(false);
        setIsPaymentProcessing(false);

        return true;
    };

    const createPaymentMethod = async (): Promise<string | null> => {
        try {
            setIsCreatePaymentMethodLoading(true);
            setErrorMessage(null);

            if (!braintreeInstance) {
                return null;
            }
            const { nonce } = await braintreeInstance.requestPaymentMethod();

            if (paymentMethodIdByNonce[nonce]) {
                setIsCreatePaymentMethodLoading(false);
                return paymentMethodIdByNonce[nonce];
            }
            const response = await createPaymentMethodApi(orgId, nonce);

            setPaymentMethodIdByNonce({ ...paymentMethodIdByNonce, [nonce]: response.paymentMethodId });
            setIsCreatePaymentMethodLoading(false);
            setPaymentMethodId(response.paymentMethodId);

            return response.paymentMethodId;
        } catch (e) {
            setHasCreatePaymentMethodFailure(true);
            setIsCreatePaymentMethodLoading(false);
            setErrorMessage('Credit card is invalid');
            return null;
        }
    };

    const clearErrorMessage = () => {
        setErrorMessage(null);
    };

    useEffect(() => {
        const fetchToken = async () => {
            const response = await getBraintreeClientToken();

            if (response?.token) {
                setClientToken(response.token);
            }

            setIsLoading(false);
        };

        fetchToken();
    }, []);

    return {
        setBraintreeInstance,
        isPaymentProcessing,
        setIsPaymentProcessing,
        clientToken,
        isLoading,
        processBraintree,
        processPurchase,
        hasPaymentSuccess,
        setHasPaymentSuccess,
        isCreatePaymentMethodLoading,
        hasCreatePaymentMethodFailure,
        createPaymentMethod,
        paymentMethodId,
        hasPaymentFailure,
        setHasPaymentFailure,
        errorMessage,
        paymentMethodIdByNonce,
        clearErrorMessage
    };
}
